import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Container, 
  Row, 
  Col, 
  Card, 
  Form, 
  Button, 
  Badge, 
  Nav,
  Alert, 
  ListGroup, 
  Table
} from 'react-bootstrap';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { 
  FaArrowLeft, 
  FaEdit, 
  FaSave, 
  FaVial, 
  FaHistory, 
  FaTimes, 
  FaServer,
  FaCheckCircle,
  FaTimesCircle, 
  FaPlus,
  FaCode,
  FaFileAlt,
  FaSync,
  FaList,
  FaCogs,
  FaProjectDiagram,
  FaInfoCircle
} from 'react-icons/fa';
import * as api from '../services/api';
import Spinner from '../components/Spinner';
import './MicroAgentDevelopment.css';

const MicroServiceDevelopment = () => {
  const { agentId, microServiceId } = useParams();
  const navigate = useNavigate();
  
  const [microService, setMicroService] = useState(null);
  const [parentAgent, setParentAgent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');

  // Enhanced helper function to safely extract data from deeply nested response structures
  const extractFromResponse = (response, ...keys) => {
    if (!response) return null;
    
    console.log('Extracting from response structure:', Object.keys(response));
    
    // Enhanced paths to handle the new test_results structure
    const paths = [
      // Direct paths
      response,
      response.response,
      response.body,
      response.data,
      
      // New nested test_results paths
      response.body?.data?.test_results?.endpoint_response,
      response.data?.test_results?.endpoint_response,
      
      // Handle the nested body that might be a JSON string
      (() => {
        try {
          // Check if we have the test_results structure with a string body
          if (response.body?.data?.test_results?.endpoint_response?.body && 
              typeof response.body.data.test_results.endpoint_response.body === 'string') {
            console.log('Found test_results with string body, parsing...');
            return JSON.parse(response.body.data.test_results.endpoint_response.body);
          }
          
          if (response.data?.test_results?.endpoint_response?.body && 
              typeof response.data.test_results.endpoint_response.body === 'string') {
            console.log('Found test_results with string body in data, parsing...');
            return JSON.parse(response.data.test_results.endpoint_response.body);
          }
          
          return null;
        } catch (e) {
          console.warn('Failed to parse nested JSON body:', e);
          return null;
        }
      })()
    ].filter(Boolean);
    
    for (const path of paths) {
      if (!path) continue;
      
      // If path is a string, try to parse it
      let currentPath = path;
      if (typeof currentPath === 'string') {
        try {
          currentPath = JSON.parse(currentPath);
        } catch (e) {
          continue;
        }
      }
      
      // Try to find the requested keys in this path
      let value = currentPath;
      for (const key of keys) {
        if (value && typeof value === 'object' && key in value) {
          value = value[key];
        } else {
          value = null;
          break;
        }
      }
      
      if (value !== null && value !== undefined) {
        console.log(`Successfully extracted data from path for keys: ${keys.join('.')}`);
        return value;
      }
    }
    
    console.warn(`Could not extract data for keys: ${keys.join('.')}`);
    return null;
  };

  useEffect(() => {
    const fetchMicroServiceDetails = async () => {
      if (!agentId || !microServiceId) return;
      
      try {
        setLoading(true);
        setError(null);
        
        // Fetch parent agent details using new API
        try {
          console.log('Fetching parent agent details...');
          const agentsResponse = await api.getAgents();
          
          if (agentsResponse && agentsResponse.success && agentsResponse.agents) {
            const parentAgentData = agentsResponse.agents.find(a => 
              a.uniqueIdentifier === agentId || 
              a.agentName === agentId || 
              a.repoName === agentId
            );
            
            if (parentAgentData) {
              setParentAgent({
                id: parentAgentData.uniqueIdentifier,
                name: parentAgentData.agentName || parentAgentData.repoName,
                description: parentAgentData.agentDescription,
                repoName: parentAgentData.repoName || "AgentDeveloper",
                awsAccount: parentAgentData.awsAccountNumber || "851725399969",
                awsRegion: parentAgentData.awsRegion || "us-west-2",
                repoAccount: parentAgentData.repoAccount || "materassi"
              });
            }
          }
        } catch (err) {
          console.error('Error fetching parent agent details:', err);
          // Set fallback parent agent
          setParentAgent({
            id: agentId,
            name: "AgentDeveloper",
            repoName: "AgentDeveloper",
            awsAccount: "851725399969",
            awsRegion: "us-west-2",
            repoAccount: "materassi"
          });
        }

        // Fetch micro service details
        try {
          console.log(`Fetching micro service details for micro_service_id: ${microServiceId}`);
          
          // Make direct API call to get micro-services
          const microServiceResponse = await api.getMicroServices(agentId || "1");
          
          console.log('Raw micro-service response from new API:', microServiceResponse);
          
          // Find the specific micro service by ID
          let microServiceData = null;
          if (microServiceResponse && microServiceResponse.success && microServiceResponse.microServices) {
            microServiceData = microServiceResponse.microServices.find(ms => 
              String(ms.id) === String(microServiceId) ||
              String(ms.micro_service_id) === String(microServiceId)
            );
          }
          
          if (microServiceData) {
            console.log('Found micro-service data:', microServiceData);
            
            const normalizedMicroService = {
              id: microServiceData.id || microServiceData.micro_service_id,
              name: microServiceData.name,
              description: microServiceData.description,
              endpoint: microServiceData.endpoint,
              method: microServiceData.method,
              devStatus: microServiceData.devStatus || (microServiceData.deployed_development ? 'deployed' : 'not deployed'),
              prodStatus: microServiceData.prodStatus || (microServiceData.deployed_production ? 'deployed' : 'not deployed'),
              status: microServiceData.status,
              active: microServiceData.active,
              agent_id: microServiceData.agent_id,
              agent_name: microServiceData.agent_name,
              scope: microServiceData.scope
            };
            
            console.log('Normalized micro-service data:', normalizedMicroService);
            setMicroService(normalizedMicroService);
          } else {
            console.error(`Micro-service with ID ${microServiceId} not found`);
            throw new Error('Micro service not found');
          }
        } catch (err) {
          console.error('Error fetching micro service details:', err);
          setError('Failed to load micro service details. Please try again.');
          throw err;
        }
        
        setLoading(false);
      } catch (error) {
        console.error('Error fetching microService details:', error);
        setError('Failed to load micro service details. Please try again.');
        setLoading(false);
      }
    };
    
    fetchMicroServiceDetails();
  }, [agentId, microServiceId, navigate]);

  const handleGoBackToParent = () => {
    navigate(`/parent/${agentId}`);
  };

  const handleGoToTesting = () => {
    navigate(`/agents/${agentId}/micro-services/${microServiceId}/testing`);
  };

  const handleTabChange = (tabKey) => {
    setActiveTab(tabKey);
  };

  const capitalizeFirstLetter = (string) => {
    if (!string) return '';
    return string.charAt(0).toUpperCase() + string.slice(1);
  };

  if (loading) {
    return (
      <Container className="micro-agent-dev-container">
        <div className="loading-container">
          <Spinner />
          <p className="mt-3">Loading micro service details...</p>
        </div>
      </Container>
    );
  }

  if (error) {
    return (
      <Container className="micro-agent-dev-container">
        <div className="error-container">
          <h4 className="error-heading">Error Loading Micro Service</h4>
          <p>{error}</p>
          <hr />
          <div className="d-flex justify-content-end">
            <Button variant="primary" onClick={handleGoBackToParent}>
              <FaArrowLeft className="me-2" />
              Back to Parent Agent
            </Button>
          </div>
        </div>
      </Container>
    );
  }

  return (
    <Container fluid className="micro-agent-dev-container">
      <ToastContainer position="top-right" autoClose={3000} />
      
      <div className="agent-info-header">
        <div className="agent-title-container">
          <h1 className="agent-title">
            {microService?.name ? capitalizeFirstLetter(microService.name) : 'Unnamed Service'}
          </h1>
          <p className="agent-description">{microService?.description || 'No description available'}</p>
        </div>
        
        <div className="agent-meta-container">
          <div className="agent-meta-row">
            <div className="meta-item">
              <span className="meta-label">Parent Agent:</span>
              <span className="meta-value">{parentAgent?.name || microService?.agent_name}</span>
            </div>
            
            <div className="meta-item">
              <span className="meta-label">Service ID:</span>
              <span className="meta-value">
                <span className="agent-number-badge">{microService?.id}</span>
              </span>
            </div>
            
            <div className="meta-item">
              <span className="meta-label">Endpoint:</span>
              <span className="meta-value">{microService?.endpoint}</span>
            </div>
            
            <div className="meta-item">
              <span className="meta-label">Method:</span>
              <span className="meta-value">
                <Badge bg="info">{microService?.method || 'N/A'}</Badge>
              </span>
            </div>
          </div>
          
          <div className="deployment-status-row">
            <div className="deployment-status">
              <span className="status-label">Development:</span>
              <Badge bg={microService?.devStatus === 'deployed' ? 'success' : 'secondary'} className="status-badge">
                {microService?.devStatus === 'deployed' ? 'Deployed' : 'Not Deployed'}
              </Badge>
            </div>
            
            <div className="deployment-status">
              <span className="status-label">Production:</span>
              <Badge bg={microService?.prodStatus === 'deployed' ? 'success' : 'secondary'} className="status-badge">
                {microService?.prodStatus === 'deployed' ? 'Deployed' : 'Not Deployed'}
              </Badge>
            </div>
          </div>
        </div>
        
        <div className="action-buttons-container">
          <Button variant="outline-light" onClick={handleGoBackToParent} className="action-button">
            <FaArrowLeft className="me-2" />
            Back to Parent Agent
          </Button>
          
          <Button variant="outline-light" onClick={handleGoToTesting} className="action-button">
            <FaVial className="me-2" />
            Test Service
          </Button>
        </div>
      </div>

      <div className="content-container">
        <Nav variant="tabs" activeKey={activeTab} onSelect={handleTabChange} className="main-tabs">
          <Nav.Item>
            <Nav.Link eventKey="overview">Overview</Nav.Link>
          </Nav.Item>
        </Nav>

        {activeTab === 'overview' && (
          <div className="p-4">
            <Card className="mb-4">
              <Card.Header>
                <h3 className="mb-0">Service Information</h3>
              </Card.Header>
              <Card.Body>
                <Row>
                  <Col md={6}>
                    <div className="mb-3">
                      <strong>Service Name:</strong>
                      <p>{microService?.name || 'N/A'}</p>
                    </div>
                    <div className="mb-3">
                      <strong>Description:</strong>
                      <p>{microService?.description || 'No description available'}</p>
                    </div>
                    <div className="mb-3">
                      <strong>Endpoint:</strong>
                      <p><code>{microService?.endpoint || 'N/A'}</code></p>
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className="mb-3">
                      <strong>Method:</strong>
                      <p><Badge bg="info">{microService?.method || 'N/A'}</Badge></p>
                    </div>
                    <div className="mb-3">
                      <strong>Status:</strong>
                      <p><Badge bg={microService?.active ? 'success' : 'secondary'}>
                        {microService?.active ? 'Active' : 'Inactive'}
                      </Badge></p>
                    </div>
                    <div className="mb-3">
                      <strong>Scope:</strong>
                      <p>{microService?.scope || 'N/A'}</p>
                    </div>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </div>
        )}
      </div>
    </Container>
  );
};

export default MicroServiceDevelopment;